import React, { useEffect, useState } from 'react';
import { IconButton, IconTextButton } from '../ui';

const AssignEmployeeTable = ({ 
  assignedEmployees, 
  onAddEmployee,
  onRemoveEmployee, 
  onDeleteEmployee,
  onNoteChange
}) => {
  const [employeeNotes, setEmployeeNotes] = useState({});
  const [focusedInputs, setFocusedInputs] = useState({});
  const [savingNotes, setSavingNotes] = useState({});

  // Initialize notes when employees are assigned
  useEffect(() => {
    console.log('🔍 AssignEmployeeTable - Processing assignedEmployees:', assignedEmployees);
    const newNotes = {};
    assignedEmployees.forEach((employee, index) => {
      const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id || index;
      
      // Check for existing notes from backend data
      const existingNote = employee?.notes || employee?.user_notes || employee?.note || '';
      
      console.log('🔍 Employee note data:', {
        employeeKey,
        employee,
        existingNote,
        notes: employee?.notes,
        user_notes: employee?.user_notes,
        note: employee?.note
      });
      
      // Always set the note from the employee data (this handles both new and existing employees)
      newNotes[employeeKey] = existingNote;
    });
    
    console.log('🔍 Setting employee notes:', newNotes);
    
    // Update the notes state with the current employee data
    setEmployeeNotes(prev => {
      // Only update if there are actual changes to avoid unnecessary re-renders
      const hasChanges = Object.keys(newNotes).some(key => prev[key] !== newNotes[key]);
      return hasChanges ? { ...prev, ...newNotes } : prev;
    });
  }, [assignedEmployees]);

  const handleNoteChange = (employee, newNote) => {
    const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id;
    
    // Show saving indicator
    setSavingNotes(prev => ({
      ...prev,
      [employeeKey]: true
    }));
    
    setEmployeeNotes(prev => ({
      ...prev,
      [employeeKey]: newNote
    }));
    
    // Update the employee object with the new note
    const updatedEmployee = {
      ...employee,
      notes: newNote,
      user_notes: newNote
    };
    
    // Call parent callback if provided
    if (onNoteChange) {
      onNoteChange(updatedEmployee, newNote);
    }
    
    // Hide saving indicator after a short delay
    setTimeout(() => {
      setSavingNotes(prev => ({
        ...prev,
        [employeeKey]: false
      }));
    }, 500);
  };

  const handleNoteFocus = (employee) => {
    const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id;
    setFocusedInputs(prev => ({
      ...prev,
      [employeeKey]: true
    }));
  };

  const handleNoteBlur = (employee) => {
    const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id;
    setFocusedInputs(prev => ({
      ...prev,
      [employeeKey]: false
    }));
  };

  const handleDeleteEmployee = (index) => {
    console.log('Delete button clicked for employee at index:', index);
    const employeeToDelete = assignedEmployees[index];
    console.log('Employee to delete:', employeeToDelete);
    
    // Remove employee note from local state
    const employeeKey = typeof employeeToDelete === 'string' ? employeeToDelete : employeeToDelete?.user_id || employeeToDelete?.employee_id;
    setEmployeeNotes(prev => {
      const newNotes = { ...prev };
      delete newNotes[employeeKey];
      return newNotes;
    });
    
    // Call the parent's remove function
    console.log('Calling onRemoveEmployee with index:', index);
    onRemoveEmployee(index);
  };

  const handlePasteToAll = (sourceEmployee, sourceNote) => {
    // Only paste if the source note is not empty
    if (!sourceNote || sourceNote.trim() === '' || sourceNote === 'Add note...') {
      return;
    }

    // Get the source employee key
    const sourceEmployeeKey = typeof sourceEmployee === 'string' ? sourceEmployee : sourceEmployee?.user_id || sourceEmployee?.employee_id;
    
    // Update notes for all employees who have empty notes
    const updatedNotes = { ...employeeNotes };
    let hasUpdates = false;

    assignedEmployees.forEach((employee) => {
      const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id;
      
      // Skip the source employee
      if (employeeKey === sourceEmployeeKey) {
        return;
      }

      // Get current note for this employee
      const currentNote = updatedNotes[employeeKey] || '';
      
      // Only update if the note is empty or is the placeholder
      if (!currentNote || currentNote.trim() === '' || currentNote === 'Add note...') {
        updatedNotes[employeeKey] = sourceNote;
        hasUpdates = true;
        
        // Update the employee object and call parent callback
        const updatedEmployee = {
          ...employee,
          notes: sourceNote,
          user_notes: sourceNote
        };
        
        if (onNoteChange) {
          onNoteChange(updatedEmployee, sourceNote);
        }
      }
    });

    // Update local state if there were any changes
    if (hasUpdates) {
      setEmployeeNotes(updatedNotes);
    }
  };

  return (
    <div className="self-stretch flex flex-col justify-start items-start gap-6">
      {/* Assign Employees Heading and Add Employee Button */}
      <div className="self-stretch flex flex-col justify-start items-start gap-2">
        <div className="self-stretch inline-flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-0">
          <div className="justify-start text-zinc-500 text-xl font-bold font-['Figtree'] leading-normal">
            Assign Employees
          </div>
          <IconTextButton 
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4V20M4 12H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            text="Add Employee"
            onClick={onAddEmployee}
            className="w-full md:w-auto md:min-w-[12rem] h-12"
          />
        </div>

        {/* Show message when no employees are assigned */}
        {assignedEmployees.length === 0 && (
          <div className="self-stretch p-3 bg-neutral-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-white/0 flex flex-col justify-start items-start gap-2.5">
            <div className="justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
              No Employee has been assigned
            </div>
          </div>
        )}
      </div>

      {/* Employee and Note Table - Only show when employees are assigned */}
      {assignedEmployees.length > 0 && (
        <div className="self-stretch rounded-xl shadow-[-2px_-2px_6px_0px_rgba(211,211,211,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] outline outline-1 outline-offset-[-1px] outline-cyan-700/10 backdrop-blur-[20px] inline-flex flex-col justify-start items-start overflow-hidden">
          {/* Table Header */}
          <div className="self-stretch bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] border-b border-black inline-flex justify-between items-center overflow-hidden">
            <div className="flex-1 h-16 relative overflow-hidden">
              <div className="left-[16px] top-[20px] absolute justify-start text-black text-xl font-bold font-['Figtree'] leading-normal">
                Employee
              </div>
            </div>
            <div className="flex-1 h-16 relative overflow-hidden">
              <div className="left-[16px] top-[20px] absolute justify-start text-black text-xl font-bold font-['Figtree'] leading-normal">
                Note
              </div>
            </div>
          </div>

          {/* Table Rows - Dynamic based on assigned employees */}
          {assignedEmployees.map((employee, index) => (
            <div key={index} className="self-stretch bg-white border-b border-zinc-300 inline-flex justify-between items-center overflow-hidden">
              <div className="flex-1 h-14 relative overflow-hidden">
                <div className="left-[16px] top-[16px] absolute justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
                  {typeof employee === 'string' ? employee : employee?.display_name || employee?.user_name || 'Unknown Employee'}
                </div>
                <IconButton 
                  onClick={() => handleDeleteEmployee(index)}
                  icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6L18 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  }
                  className="absolute right-4 top-3 hover:bg-red-600 transition-colors duration-200 cursor-pointer"
                  title="Remove employee"
                />
              </div>
              <div className="flex-1 h-14 relative overflow-hidden">
                <input
                  type="text"
                  value={employeeNotes[typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id] || ''}
                  onChange={(e) => handleNoteChange(employee, e.target.value)}
                  onFocus={() => handleNoteFocus(employee)}
                  onBlur={() => handleNoteBlur(employee)}
                  className="left-[16px] top-[16px] absolute justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed bg-transparent border-none outline-none w-3/4"
                  placeholder="Add note..."
                />
                {/* Saving indicator */}
                {savingNotes[typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id] && (
                  <div className="absolute right-20 top-4 text-xs text-green-600 font-medium">
                    Saving...
                  </div>
                )}
                {/* Paste to All button - only show if employee has a note */}
                {(() => {
                  const employeeKey = typeof employee === 'string' ? employee : employee?.user_id || employee?.employee_id;
                  const currentNote = employeeNotes[employeeKey] || '';
                  const hasNote = currentNote && currentNote.trim() !== '' && currentNote !== 'Add note...';
                  
                  return hasNote ? (
                    <IconButton
                      onClick={() => handlePasteToAll(employee, currentNote)}
                      icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <rect x="8" y="2" width="8" height="4" rx="1" ry="1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      }
                      className="absolute right-4 top-3 hover:bg-green-600 transition-colors duration-200"
                      title="Paste note to all employees with empty notes"
                    />
                  ) : null;
                })()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssignEmployeeTable;
